import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { RegisterPageEnhanced as RegisterPage } from './pages/RegisterPageEnhanced';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import Dashboard from './components/Dashboard';
import { LostRevenueDetailPage } from './pages/LostRevenueDetailPage';
import { UserManagementPage } from "./pages/admin/UserManagementPage";
import { LogViewerPage } from "./pages/admin/LogViewerPage";
import { ReportRecipientsPage } from "./pages/admin/ReportRecipientsPage";
import RaceEntry from './components/RaceEntry';
import Standings from './components/Standings';
import DriverManagement from './components/DriverManagement';
import DriverLocationScraper from './components/DriverLocationScraper';
import TrackManagement from './components/TrackManagement';
import DivisionManagement from './components/DivisionManagement';
import RegionManagement from './components/RegionManagement';
import PointsSchema from './components/PointsSchema';
import TrackAssignments from './components/TrackAssignments';
import Reports from './components/Reports';
import DivisionMappingManagement from './components/DivisionMappingManagement';
import SeasonSetup from './components/SeasonSetup';
import { useAuth } from './context/AuthContext';
import { setGlobalNavigateToLogin } from './utils/apiUtils';

// HomePage component that redirects authenticated users to dashboard
const HomePage: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <p>Loading...</p>
      </div>
    );
  }

  // Redirect authenticated users to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show welcome message for unauthenticated users
  return (
    <div style={{ textAlign: 'center', padding: '2rem' }}>
      <h1>Welcome to DIRTcar Points!</h1>
      <p>Please login or register to continue.</p>
      <div style={{ marginTop: '2rem' }}>
        <a href="/login" style={{ marginRight: '1rem', textDecoration: 'none', color: '#1976d2' }}>
          Login
        </a>
        <a href="/register" style={{ textDecoration: 'none', color: '#1976d2' }}>
          Register
        </a>
      </div>
    </div>
  );
};

// Component to set up global navigation handler for API utils
const GlobalNavigationSetup: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();

  useEffect(() => {
    // Set up global navigation handler for token expiration
    setGlobalNavigateToLogin(() => {
      navigate('/login', { replace: true });
    });
  }, [navigate]);

  return <>{children}</>;
};

function App() {
  const location = useLocation();
  return (
    <GlobalNavigationSetup>
      <Layout>
        <ErrorBoundary key={location.pathname}>
        <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

        {/* Protected Routes */}
        {/* General Authenticated Routes */}
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dashboard/lost-revenue/:divisionId" element={<LostRevenueDetailPage />} />
          <Route path="/race-entry" element={<RaceEntry />} />
          <Route path="/standings" element={<Standings />} />
          <Route path="/drivers" element={<DriverManagement />} />
          <Route path="/reports" element={<Reports />} />
          {/* Add other general protected routes here, e.g.: */}
          {/* <Route path="/profile" element={<ProfilePage />} /> */}
          {/* <Route path="/settings" element={<SettingsPage />} /> */}
        </Route>

        {/* Admin Only Protected Routes */}
        <Route element={<ProtectedRoute adminOnly={true} />}>
          <Route path="/admin/user-management" element={<UserManagementPage />} />
          <Route path="/admin/report-recipients" element={<ReportRecipientsPage />} />
          <Route path="/admin/logs" element={<LogViewerPage />} />
          <Route path="/drivers/location-scraper" element={<DriverLocationScraper />} />
          <Route path="/tracks" element={<TrackManagement />} />
          <Route path="/divisions" element={<DivisionManagement />} />
          <Route path="/regions" element={<RegionManagement />} />
          <Route path="/points-schemas" element={<PointsSchema />} />
          <Route path="/track-assignments" element={<TrackAssignments />} />
          <Route path="/division-mappings" element={<DivisionMappingManagement />} />
          <Route path="/season-setup" element={<SeasonSetup />} />
          <Route path="/season-management" element={<Navigate to="/season-setup" replace />} />
          {/* Add other admin-only routes here */}
        </Route>

        {/* Catch-all or Not Found Route */}
        <Route path="*" element={<Navigate to="/" replace />} />
        {/* Or a custom 404 component: <Route path="*" element={<NotFoundPage />} /> */}
      </Routes>
        </ErrorBoundary>
    </Layout>
    </GlobalNavigationSetup>
  );
}

export default App;
